import {
  getFileDetail,
  getFileLazyTreeView,
  getNodeChildren,
} from '@/components/File/FileTree/service';
import { fetchFavorites, getGroups } from '@/services/file.service';
import { isUsableFileNode } from '@/utils/fileStatus';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface FileTreeNode {
  key: string;
  title: string;
  children?: FileTreeNode[];
  is_folder?: boolean;
  hasChildren?: boolean;
  loaded?: boolean;
  [key: string]: any;
}

// Cached node structure - store nodes separately
interface CachedNode {
  id: string;
  title: string;
  hasChildren: boolean;
  is_folder: boolean;
  childrenIds?: string[]; // IDs of children
  [key: string]: any;
}

interface FileTreeContextType {
  treeData: FileTreeNode[] | null;
  loading: boolean;
  error: string | null;
  refreshTreeData: () => Promise<void>;
  refreshNodeChildren: (key: string) => Promise<void>;
  updateNodeChildrenFromData: (key: string, files: any[]) => void;
  setTreeData: (data: FileTreeNode[] | null) => void;
  loadData: (node: { key: React.Key; children?: FileTreeNode[] }) => Promise<void>;
  handleNodeExpand: (expandedKeys: React.Key[], info: { expanded: boolean; node: any }) => void;
  handleNodeSelect: (selectedKeys: React.Key[], info: { selected: boolean; node: any }) => void;
  updateNodeTitle: (key: string, title: string) => void;
  addNodeToParent: (
    parentKey: string,
    node: { key: string; title: string; is_folder?: boolean; [k: string]: any },
  ) => boolean;
  removeNode: (key: string, parentKey: string) => void;
  findParentKey: (key: string) => string | null;
  loadFolderRecursively: (key: string) => Promise<FileTreeNode[]>;
  expandPathToFiles: (fileIds: string[]) => Promise<void>;
  expandedKeys: React.Key[];
  selectedKeys: React.Key[];
  setExpandedKeys: (keys: React.Key[]) => void;
  setSelectedKeys: (keys: React.Key[]) => void;
  favoriteFiles: FileTreeNode[];
  refreshFavorites: () => Promise<void>;
}

const FileTreeContext = createContext<FileTreeContextType | undefined>(undefined);

interface FileTreeProviderProps {
  children: ReactNode;
}

export const FileTreeProvider: React.FC<FileTreeProviderProps> = ({ children }) => {
  const [treeData, setTreeData] = useState<FileTreeNode[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [favoriteFiles, setFavoriteFiles] = useState<FileTreeNode[]>([]);

  const refreshFavorites = useCallback(async () => {
    try {
      const res = await fetchFavorites();
      if (res && res.success && res.data) {
        const nodes: FileTreeNode[] = res.data.map((file: any) => {
          // Favourites can be folders (or groups) as well as files - trust the
          // API's `type` instead of hardcoding every favourite as a file, else
          // favourited folders render/behave as (broken) files in the popup.
          const isFolder = file.type === 'folder' || file.type === 'group';
          return {
            key: file._id,
            title: file.name,
            is_folder: isFolder,
            isLeaf: !isFolder,
            type: isFolder ? 'folder' : 'file',
            hasChildren: isFolder,
            fileUrl: file.file_url,
            status: file.status,
          };
        });
        setFavoriteFiles(nodes.filter(isUsableFileNode));
      }
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
    }
  }, []);

  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);

  // Node-based cache - source of truth
  const nodeCache = useRef<Map<string, CachedNode>>(new Map());

  // Cache for file details to avoid repeated API calls
  const fileDetailCache = useRef<Map<string, any>>(new Map());

  // Cache for rootFolderId → groupId mapping from /api/files/groups/
  const rootFolderToGroupMap = useRef<Map<string, string> | null>(null);

  // Build tree view from cache
  const buildTreeFromCache = useCallback((rootIds: string[]): FileTreeNode[] => {
    const buildNode = (id: string): FileTreeNode | null => {
      const cached = nodeCache.current.get(id);
      if (!cached) return null;

      const node: FileTreeNode = {
        ...cached,
        key: cached.id,
        isLeaf: !cached.is_folder,
        loaded: cached.childrenIds !== undefined,
      };

      // If children are loaded, build them
      if (cached.childrenIds && cached.childrenIds.length > 0) {
        node.children = cached.childrenIds
          .map((childId) => buildNode(childId))
          .filter((child) => child !== null) as FileTreeNode[];
      }

      return node;
    };

    return rootIds.map((id) => buildNode(id)).filter((node) => node !== null) as FileTreeNode[];
  }, []);

  // Store nodes in cache
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cacheNodes = useCallback((nodes: any[], _parentId?: string) => {
    const nodeIds: string[] = [];

    nodes.forEach((node) => {
      nodeCache.current.set(node.key, {
        id: node.key,
        title: node.title,
        hasChildren: node.hasChildren ?? false,
        is_folder: node.is_folder ?? false,
        childrenIds: nodeCache.current.get(node.key)?.childrenIds, // Preserve existing children
        ...node,
      });
      nodeIds.push(node.key);
    });

    return nodeIds;
  }, []);

  const refreshTreeData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getFileLazyTreeView();
      const rootNodes = data.data || [];

      // Cache root nodes
      const rootIds = cacheNodes(rootNodes);

      // Build tree from cache
      const tree = buildTreeFromCache(rootIds);
      setTreeData(tree);
    } catch (err) {
      console.error('Failed to fetch tree data:', err);
      setError('Failed to load file tree data');
    } finally {
      setLoading(false);
    }
  }, [cacheNodes, buildTreeFromCache]);

  const loadNodeChildren = useCallback(
    async (key: string) => {
      try {
        const data = await getNodeChildren(key);
        const children = data.data || [];

        // Cache children nodes
        const childrenIds = cacheNodes(children, key);

        // Update parent node with children IDs
        const parentNode = nodeCache.current.get(key);
        if (parentNode) {
          nodeCache.current.set(key, {
            ...parentNode,
            childrenIds,
          });
        }

        // Rebuild tree from cache - only rebuild if node is in current tree
        const findAndRebuild = (nodes: FileTreeNode[]): FileTreeNode[] => {
          return nodes.map((node) => {
            if (node.key === key) {
              // Rebuild this node with its children
              const cached = nodeCache.current.get(key);
              if (cached && cached.childrenIds) {
                return {
                  ...node,
                  children: buildTreeFromCache(cached.childrenIds),
                  loaded: true,
                };
              }
            }
            if (node.children) {
              return {
                ...node,
                children: findAndRebuild(node.children),
              };
            }
            return node;
          });
        };

        setTreeData((prevData) => (prevData ? findAndRebuild(prevData) : null));
      } catch (err) {
        console.error('Failed to load node children:', err);
      }
    },
    [cacheNodes, buildTreeFromCache],
  );

  const refreshNodeChildren = useCallback(
    async (key: string) => {
      await loadNodeChildren(key);
    },
    [loadNodeChildren],
  );

  const isFolderType = useCallback((type: string) => type !== 'file', []);

  const updateNodeChildrenFromData = useCallback(
    (key: string, files: any[]) => {
      // Map raw file data to the same format as getNodeChildren
      console.log('update node', key, files);
      const children = files
        .map((file: any) => {
          const isFolder = isFolderType(file.type);
          return {
            key: file.id,
            title: file.name,
            is_folder: isFolder,
            hasChildren: file.hasChildren !== undefined ? file.hasChildren : undefined,
            isLeaf: !isFolder,
            ...file,
          };
        })
        .filter(isUsableFileNode);

      // Cache children nodes
      const childrenIds = cacheNodes(children, key);

      // Update parent node with children IDs
      const parentNode = nodeCache.current.get(key);
      if (parentNode) {
        nodeCache.current.set(key, {
          ...parentNode,
          childrenIds,
        });
      }

      // Rebuild the affected subtree in the tree data
      const findAndRebuild = (nodes: FileTreeNode[]): FileTreeNode[] => {
        return nodes.map((node) => {
          if (node.key === key) {
            const cached = nodeCache.current.get(key);
            if (cached && cached.childrenIds) {
              return {
                ...node,
                children: buildTreeFromCache(cached.childrenIds),
                loaded: true,
              };
            }
          }
          if (node.children) {
            return {
              ...node,
              children: findAndRebuild(node.children),
            };
          }
          return node;
        });
      };

      setTreeData((prevData) => (prevData ? findAndRebuild(prevData) : null));
    },
    [cacheNodes, buildTreeFromCache, isFolderType],
  );

  const updateNodeTitle = useCallback((key: string, title: string) => {
    // Update cache
    const cached = nodeCache.current.get(key);
    if (cached) {
      nodeCache.current.set(key, { ...cached, title });
    }

    // Update tree data in place
    const updateInTree = (nodes: FileTreeNode[]): FileTreeNode[] =>
      nodes.map((node) => {
        if (node.key === key) {
          return { ...node, title, name: title };
        }
        if (node.children) {
          return { ...node, children: updateInTree(node.children) };
        }
        return node;
      });

    setTreeData((prevData) => (prevData ? updateInTree(prevData) : null));
  }, []);

  const addNodeToParent = useCallback(
    (
      parentKey: string,
      node: { key: string; title: string; is_folder?: boolean; [k: string]: any },
    ): boolean => {
      // Check if parent exists in cache (it won't if it was filtered out as an empty folder)
      const parentInCache = nodeCache.current.has(parentKey);

      // Add child to cache
      nodeCache.current.set(node.key, {
        id: node.key,
        hasChildren: node.is_folder ?? false,
        is_folder: node.is_folder ?? false,
        ...node,
      });

      if (!parentInCache) {
        // Parent was never loaded into the tree (e.g. empty folder filtered out)
        // Caller should refresh the grandparent to make this folder appear
        return false;
      }

      // Update parent's childrenIds and mark it as having children
      const parent = nodeCache.current.get(parentKey)!;
      const existingIds = parent.childrenIds || [];
      if (!existingIds.includes(node.key)) {
        nodeCache.current.set(parentKey, {
          ...parent,
          hasChildren: true,
          childrenIds: [...existingIds, node.key],
        });
      }

      // Rebuild affected subtree
      const insertInTree = (nodes: FileTreeNode[]): FileTreeNode[] =>
        nodes.map((n) => {
          if (n.key === parentKey) {
            const newChild: FileTreeNode = {
              ...node,
              key: node.key,
              isLeaf: !node.is_folder,
              loaded: true,
            };
            return {
              ...n,
              isLeaf: false,
              loaded: true,
              children: [...(n.children || []), newChild],
            };
          }
          if (n.children) {
            return { ...n, children: insertInTree(n.children) };
          }
          return n;
        });

      setTreeData((prevData) => (prevData ? insertInTree(prevData) : null));
      return true;
    },
    [],
  );

  const removeNode = useCallback((key: string, parentKey: string) => {
    // Remove from cache
    nodeCache.current.delete(key);

    // Update parent's childrenIds
    const parent = nodeCache.current.get(parentKey);
    if (parent && parent.childrenIds) {
      nodeCache.current.set(parentKey, {
        ...parent,
        childrenIds: parent.childrenIds.filter((id) => id !== key),
      });
    }

    // Remove from tree
    const removeFromTree = (nodes: FileTreeNode[]): FileTreeNode[] =>
      nodes
        .filter((n) => n.key !== key)
        .map((n) => {
          if (n.children) {
            return { ...n, children: removeFromTree(n.children) };
          }
          return n;
        });

    setTreeData((prevData) => (prevData ? removeFromTree(prevData) : null));
  }, []);

  const findParentKey = useCallback((key: string): string | null => {
    for (const [id, node] of nodeCache.current.entries()) {
      if (node.childrenIds?.includes(key)) {
        return id;
      }
    }
    return null;
  }, []);

  const loadData = useCallback(
    async (node: { key: React.Key; children?: FileTreeNode[] }) => {
      // Check if already loaded in cache
      const cached = nodeCache.current.get(String(node.key));
      if (cached && cached.childrenIds !== undefined) {
        return;
      }

      await loadNodeChildren(String(node.key));
    },
    [loadNodeChildren],
  );

  const fetchChildrenRecursively = useCallback(
    async (key: string): Promise<FileTreeNode[]> => {
      // Check cache first
      const cached = nodeCache.current.get(key);
      if (cached && cached.childrenIds) {
        // Already loaded, return from cache
        return buildTreeFromCache(cached.childrenIds);
      }

      // Fetch children
      const result = await getNodeChildren(key);
      const children = result.data || [];

      // Cache children
      const childrenIds = cacheNodes(children, key);

      // Update parent with children IDs
      const parentNode = nodeCache.current.get(key);
      if (parentNode) {
        nodeCache.current.set(key, {
          ...parentNode,
          childrenIds,
        });
      }

      // Recursively fetch grandchildren for folders
      const processedChildren = await Promise.all(
        children.map(async (child: any) => {
          // Check if it's a folder - be more lenient with detection
          const isFolder = child.is_folder || (child.type !== 'file' && !child.isLeaf);

          if (isFolder) {
            const grandChildren = await fetchChildrenRecursively(child.key);

            // Update cache with grandchildren
            const childNode = nodeCache.current.get(child.key);
            if (childNode) {
              nodeCache.current.set(child.key, {
                ...childNode,
                childrenIds: grandChildren.map((gc) => gc.key),
              });
            }

            return {
              ...child,
              children: grandChildren,
              loaded: true,
            };
          }
          return {
            ...child,
            isLeaf: !child.is_folder,
            loaded: true,
          };
        }),
      );

      return processedChildren;
    },
    [cacheNodes, buildTreeFromCache],
  );

  const loadFolderRecursively = useCallback(
    async (key: string): Promise<FileTreeNode[]> => {
      const children = await fetchChildrenRecursively(key);

      // Update tree data
      const findAndRebuild = (nodes: FileTreeNode[]): FileTreeNode[] => {
        return nodes.map((node) => {
          if (node.key === key) {
            return {
              ...node,
              children,
              loaded: true,
            };
          }
          if (node.children) {
            return {
              ...node,
              children: findAndRebuild(node.children),
            };
          }
          return node;
        });
      };

      setTreeData((prevData) => (prevData ? findAndRebuild(prevData) : null));
      return children;
    },
    [fetchChildrenRecursively],
  );

  // Helper to load children into cache only (no tree rebuild per call)
  const loadNodeChildrenToCache = useCallback(
    async (key: string) => {
      // Skip if already loaded in cache
      const existing = nodeCache.current.get(key);
      if (existing && existing.childrenIds !== undefined) return;

      try {
        const data = await getNodeChildren(key);
        const children = data.data || [];

        // Cache children nodes
        const childrenIds = cacheNodes(children, key);

        // Update parent node with children IDs
        const parentNode = nodeCache.current.get(key);
        if (parentNode) {
          nodeCache.current.set(key, {
            ...parentNode,
            childrenIds,
          });
        }
      } catch (err) {
        console.error('Failed to load node children to cache:', err);
      }
    },
    [cacheNodes],
  );

  const expandPathToFiles = useCallback(
    async (fileIds: string[]) => {
      const paths: any[][] = [];

      // Build rootFolderId → groupId map if not yet cached
      if (!rootFolderToGroupMap.current) {
        try {
          const groupsRes = await getGroups();
          const groups = groupsRes?.data || groupsRes || [];
          const mapping = new Map<string, string>();
          groups.forEach((g: any) => {
            if (g.rootFolderId && g.id) {
              mapping.set(g.rootFolderId, g.id);
            }
          });
          rootFolderToGroupMap.current = mapping;
        } catch (e) {
          console.error('Failed to fetch groups for rootFolder mapping', e);
          rootFolderToGroupMap.current = new Map();
        }
      }

      await Promise.all(
        fileIds.map(async (id) => {
          // Check if node is in cache
          if (nodeCache.current.has(id)) return;

          try {
            let file;
            // Check file detail cache
            if (fileDetailCache.current.has(id)) {
              file = fileDetailCache.current.get(id);
            } else {
              // Fetch from API
              const res = await getFileDetail(id);
              file = res.data || res;
              fileDetailCache.current.set(id, file);
            }

            if (Array.isArray(file.path)) {
              paths.push(file.path);
            }
          } catch (e) {
            console.error(`Failed to fetch detail for ${id}`, e);
          }
        }),
      );

      if (paths.length === 0) return;

      // Exclude the last element in each path (the file itself).
      // Only parent folders need their children loaded so the tree
      // branch is visible down to the selected file.
      const folderPaths = paths.map((p) => p.slice(0, -1));

      // The first element (index 0) in each path is a rootFolderId.
      // The tree uses groupId as its key, so map rootFolderId → groupId.
      const resolvedPaths = folderPaths.map((p) => {
        if (p.length === 0) return p;
        const first = p[0];
        const groupId = rootFolderToGroupMap.current?.get(first.id);
        if (groupId) {
          return [{ ...first, id: groupId }, ...p.slice(1)];
        }
        return p;
      });

      const maxDepth = Math.max(...resolvedPaths.map((p) => p.length));

      // Collect all level-0 IDs (group IDs) from resolved paths
      const level0Ids = new Set<string>();
      resolvedPaths.forEach((p) => {
        if (p[0]) level0Ids.add(p[0].id);
      });

      // Check if any level-0 IDs are NOT root-level tree nodes.
      // If so, they're nested under a root-level type folder (e.g. firm_wide)
      // whose children haven't been loaded yet. Load all unloaded root-level
      // folder children to reveal these groups.
      const missingFromRoot = Array.from(level0Ids).filter(
        (id) => !nodeCache.current.get(id)?.childrenIds && !nodeCache.current.has(id),
      );

      if (missingFromRoot.length > 0) {
        // Load children of all root-level folder nodes to reveal nested groups
        const rootFolderIds: string[] = [];
        nodeCache.current.forEach((node) => {
          // A root-level node is one that is a folder and exists in the current tree root
          if (node.is_folder && !node.childrenIds) {
            rootFolderIds.push(node.id);
          }
        });

        if (rootFolderIds.length > 0) {
          await Promise.all(rootFolderIds.map((id) => loadNodeChildrenToCache(id)));
        }
      }

      // Load all folder children into cache level by level (no tree rebuild yet)
      for (let i = 0; i < maxDepth; i++) {
        const idsAtLevel = new Set<string>();
        resolvedPaths.forEach((p) => {
          if (p[i]) {
            idsAtLevel.add(p[i].id);
          }
        });

        if (idsAtLevel.size > 0) {
          await Promise.all(Array.from(idsAtLevel).map((id) => loadNodeChildrenToCache(id)));
        }
      }

      // After all levels are loaded into cache, rebuild the entire tree once
      setTreeData((prevData) => {
        if (!prevData) return null;
        const rebuildFromCache = (nodes: FileTreeNode[]): FileTreeNode[] => {
          return nodes.map((node) => {
            const cached = nodeCache.current.get(node.key);
            if (!cached) return node;
            const rebuilt: FileTreeNode = {
              ...cached,
              key: cached.id,
              isLeaf: !cached.is_folder,
              loaded: cached.childrenIds !== undefined,
            };
            if (cached.childrenIds && cached.childrenIds.length > 0) {
              rebuilt.children = buildTreeFromCache(cached.childrenIds);
            } else if (node.children) {
              rebuilt.children = rebuildFromCache(node.children);
            }
            return rebuilt;
          });
        };
        return rebuildFromCache(prevData);
      });
    },
    [loadNodeChildrenToCache, buildTreeFromCache],
  );

  const handleNodeExpand = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (expandedKeys: React.Key[], _info: { expanded: boolean; node: any }) => {
      setExpandedKeys(expandedKeys);
    },
    [],
  );

  const handleNodeSelect = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (selectedKeys: React.Key[], _info: { selected: boolean; node: any }) => {
      setSelectedKeys(selectedKeys);
    },
    [],
  );

  const contextValue: FileTreeContextType = {
    treeData,
    loading,
    error,
    refreshTreeData,
    refreshNodeChildren,
    updateNodeChildrenFromData,
    updateNodeTitle,
    addNodeToParent,
    removeNode,
    findParentKey,
    setTreeData,
    loadData,
    handleNodeExpand,
    handleNodeSelect,
    loadFolderRecursively,
    expandPathToFiles,
    expandedKeys,
    selectedKeys,
    setExpandedKeys,
    setSelectedKeys,
    favoriteFiles,
    refreshFavorites,
  };

  return <FileTreeContext.Provider value={contextValue}>{children}</FileTreeContext.Provider>;
};

export const useFileTree = (): FileTreeContextType => {
  const context = useContext(FileTreeContext);
  if (context === undefined) {
    throw new Error('useFileTree must be used within a FileTreeProvider');
  }
  return context;
};
